import type { DailyPoint } from '@floorpoll/shared';

const WIDTH = 780;
const HEIGHT = 168;
const BASELINE = 132;
const TOP = 14;
const LEFT = 30;
const RIGHT = 10;

/** 0 = Sunday, 6 = Saturday. Parsed as UTC, matching the keys the API builds. */
function weekday(day: string): number {
  return new Date(`${day}T00:00:00Z`).getUTCDay();
}

const isWeekend = (day: string) => weekday(day) === 0 || weekday(day) === 6;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-08-22` -> `22 Aug`. Long enough to be unambiguous in a tooltip. */
function readableDay(day: string): string {
  const [, month, date] = day.split('-') as [string, string, string];
  return `${Number(date)} ${MONTHS[Number(month) - 1] ?? ''}`.trim();
}

/**
 * Daily volume, one thin column per day.
 *
 * Columns rather than a line: each day is a discrete count, and a line drawn
 * between two days implies values in between that were never measured.
 *
 * Weekends sit on a recessed ground. That is the one piece of decoration here
 * and it earns its place — this is a product for presenting to a room, so its
 * usage has a hard weekday rhythm, and a run of quiet Saturdays is the shape
 * of a healthy week rather than a problem. Without the ground an operator
 * reads the same chart as an alarming sawtooth.
 *
 * `label` and `unit` are props rather than the console-activity wording this
 * started as: nothing about the mark is specific to actions, and a second
 * series drawn by a chart that announces "console actions" to a screen reader
 * would be wrong in the one channel that cannot see the heading above it.
 * `unit` carries both forms because a single day's count is very often 1.
 */
export function EventDayStrip({
  points,
  label,
  unit,
}: {
  points: DailyPoint[];
  /** Opens the aria-label, e.g. "Console actions per day". */
  label: string;
  unit: { one: string; many: string };
}) {
  const max = Math.max(1, ...points.map((point) => point.count));
  // A clean ceiling, so the gridline labels are round numbers.
  const step = Math.max(1, Math.ceil(max / 4));
  const ceiling = step * 4;
  const slot = (WIDTH - LEFT - RIGHT) / Math.max(1, points.length);
  const barWidth = Math.max(1.5, slot * 0.62);

  return (
    <svg
      className="usage-chart day-strip"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${label} over the last ${points.length} days. Busiest day ${max}. ${points
        .filter((point) => point.count > 0)
        .map((point) => `${point.day}: ${point.count}`)
        .join('; ')}`}
    >
      {/* Ground first, so every column and gridline sits over it. */}
      {points.map((point, index) =>
        isWeekend(point.day) ? (
          <rect
            key={`ground-${point.day}`}
            className="day-weekend"
            x={LEFT + index * slot}
            y={TOP - 6}
            width={slot}
            height={BASELINE - TOP + 6}
          />
        ) : null,
      )}

      {[0, 0.5, 1].map((fraction) => {
        const y = BASELINE - fraction * (BASELINE - TOP);
        return (
          <g key={fraction}>
            <line className="usage-grid" x1={LEFT} y1={y} x2={WIDTH - RIGHT} y2={y} />
            <text className="usage-tick" x={LEFT - 6} y={y + 3} textAnchor="end">
              {Math.round(fraction * ceiling)}
            </text>
          </g>
        );
      })}

      {points.map((point, index) => {
        const height = (point.count / ceiling) * (BASELINE - TOP);
        return (
          <rect
            key={point.day}
            className="day-bar"
            x={LEFT + index * slot + (slot - barWidth) / 2}
            // A day with no events draws nothing rather than a 1px stub: a
            // floor would make zero and one look the same, and the gap is
            // exactly what an operator is scanning for.
            y={BASELINE - height}
            width={barWidth}
            height={height}
          >
            <title>{`${readableDay(point.day)} · ${point.count} ${point.count === 1 ? unit.one : unit.many}`}</title>
          </rect>
        );
      })}

      <line className="day-axis" x1={LEFT} y1={BASELINE} x2={WIDTH - RIGHT} y2={BASELINE} />

      {/* Ticks only where a month turns over. A label per day is unreadable at
          this width, and evenly spaced labels would land mid-week and mean
          nothing. */}
      {points.map((point, index) => {
        const [, month, date] = point.day.split('-') as [string, string, string];
        if (date !== '01' && index !== 0) return null;
        return (
          <text
            key={`tick-${point.day}`}
            className="usage-tick"
            x={LEFT + index * slot}
            y={BASELINE + 15}
            textAnchor={index === 0 ? 'start' : 'middle'}
          >
            {MONTHS[Number(month) - 1]}
          </text>
        );
      })}

      <text className="usage-tick" x={WIDTH - RIGHT} y={BASELINE + 15} textAnchor="end">
        today
      </text>
    </svg>
  );
}
